import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Paper,
  Card,
  CardContent,
  Avatar,
  Divider,
  Stack,
} from '@mui/material';
import Footer from '../components/Footer';
import RestaurantMenuIcon from '@mui/icons-material/RestaurantMenu';
import QrCodeIcon from '@mui/icons-material/QrCode';
import DevicesIcon from '@mui/icons-material/Devices';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const AboutPage = () => {
  // Team members data
  const teamMembers = [
    {
      name: 'Devang Sharma',
      position: 'Founder & CEO',
      image: 'https://randomuser.me/api/portraits/men/32.jpg',
      bio: 'Despite his young age, Devang brings extraordinary vision and innovation to QResto. With a background in design and marketing, he identified the gap between traditional dining and emerging tech, creating QResto to revolutionize the restaurant experience.',
    },
    {
      name: 'Priya Patel',
      position: 'Chief Technology Officer',
      image: 'https://randomuser.me/api/portraits/women/44.jpg',
      bio: 'Priya leads our engineering team with expertise in AR technology and mobile web development to create seamless dining experiences.',
    },
    {
      name: 'Vikram Singh',
      position: 'Creative Director',
      image: 'https://randomuser.me/api/portraits/men/62.jpg',
      bio: 'Vikram brings menus to life through creative design and visualization, ensuring each AR menu is not just functional but beautiful.',
    },
    {
      name: 'Anjali Desai',
      position: 'Customer Success Lead',
      image: 'https://randomuser.me/api/portraits/women/68.jpg',
      bio: 'Anjali ensures our restaurant partners receive exceptional support and guidance throughout their QResto journey.',
    },
  ];

  // Company values
  const values = [
    {
      title: 'Innovation',
      description: `We constantly push the boundaries of what's possible in restaurant technology.`,
      icon: <QrCodeIcon fontSize="large" />,
    },
    {
      title: 'Quality',
      description: `We're committed to delivering high-quality AR experiences that delight users.`,
      icon: <RestaurantMenuIcon fontSize="large" />,
    },
    {
      title: 'Accessibility',
      description: 'We believe advanced technology should be accessible to restaurants of all sizes.',
      icon: <DevicesIcon fontSize="large" />,
    },
    {
      title: 'Customer Success',
      description: `Our partners' success is our success – we're dedicated to exceptional support.`,
      icon: <SupportAgentIcon fontSize="large" />,
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 10, md: 16 },
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontSize: { xs: '2.5rem', md: '4rem' },
                fontWeight: 800,
                mb: 3,
              }}
            >
              About QResto
            </Typography>
            <Typography variant="h5" sx={{ mb: 4, opacity: 0.9, maxWidth: '800px', mx: 'auto' }}>
              We're transforming restaurant menus with augmented reality, one QR code at a time
            </Typography>
          </Box>
        </Container>
        
        {/* Visual elements in the background */}
        <Box sx={{ 
          position: 'absolute', 
          top: -100, 
          left: -100, 
          width: 300, 
          height: 300, 
          bgcolor: 'rgba(255,255,255,0.05)',
          borderRadius: '50%' 
        }} />
        <Box sx={{ 
          position: 'absolute', 
          bottom: -80, 
          right: -80, 
          width: 250, 
          height: 250, 
          bgcolor: 'rgba(255,255,255,0.08)',
          borderRadius: '50%' 
        }} />
      </Box>
      
      {/* Our Story Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Grid container spacing={6} alignItems="center">
          <Grid item xs={12} md={6}>
            <Typography variant="h2" gutterBottom sx={{ fontWeight: 700 }}>
              Our Story
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              Founded in 2023, QResto was born from a simple observation: restaurant menus hadn't evolved in decades, despite all the technological advancements around us.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              Our founder, Devang Sharma, a young innovator with exceptional design sensibilities and marketing expertise, was dining at a restaurant when he realized the gap between the traditional paper menu and the immersive digital experiences we've come to expect in other aspects of life.
            </Typography>
            <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
              Despite his youth, Devang's forward-thinking approach and creative problem-solving led to the birth of QResto — an elegant solution that brings menus to life through augmented reality, accessible via a simple QR code scan, without requiring any app downloads.
            </Typography>
            <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
              Today, we're proud to partner with restaurants across India, helping them create memorable dining experiences through technology that's both cutting-edge and accessible, all guided by Devang's innovative vision and deep understanding of user experience design.
            </Typography>
          </Grid>
          <Grid item xs={12} md={6}>
            <Box
              component="img"
              src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
              alt="Restaurant table with QR code"
              sx={{
                width: '100%',
                borderRadius: 4,
                boxShadow: '0 20px 40px rgba(0,0,0,0.12)',
              }}
            />
          </Grid>
        </Grid>
      </Container>
      
      {/* Our Values Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography variant="overline" color="primary" fontWeight="bold">
              WHAT DRIVES US
            </Typography>
            <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
              Our Values
            </Typography>
            <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
              These core principles guide everything we do at QResto
            </Typography>
          </Box>
          
          <Grid container spacing={4}>
            {values.map((value, index) => (
              <Grid item xs={12} sm={6} md={3} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 4,
                    height: '100%',
                    borderRadius: 4,
                    bgcolor: 'background.paper',
                    textAlign: 'center',
                    transition: 'transform 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-8px)',
                      boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                    },
                  }}
                >
                  <Box sx={{ color: 'primary.main', mb: 2 }}>
                    {value.icon}
                  </Box>
                  <Typography variant="h5" component="h3" gutterBottom fontWeight="medium">
                    {value.title}
                  </Typography>
                  <Typography variant="body1" color="text.secondary">
                    {value.description}
                  </Typography>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>
      
      {/* Meet Our Team Section */}
      <Container maxWidth="lg" sx={{ py: { xs: 8, md: 12 } }}>
        <Box sx={{ textAlign: 'center', mb: 8 }}>
          <Typography variant="overline" color="primary" fontWeight="bold">
            THE PEOPLE BEHIND QRESTO
          </Typography>
          <Typography variant="h2" sx={{ mb: 2, fontWeight: 700 }}>
            Meet Our Team
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ maxWidth: '700px', mx: 'auto' }}>
            Passionate experts dedicated to transforming dining experiences
          </Typography>
        </Box>
        
        <Grid container spacing={4}>
          {teamMembers.map((member, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card sx={{ 
                height: '100%',
                borderRadius: 4,
                transition: 'transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 30px rgba(0,0,0,0.12)',
                },
              }}>
                <CardContent sx={{ textAlign: 'center', p: 3 }}>
                  <Avatar
                    src={member.image}
                    alt={member.name}
                    sx={{ 
                      width: 120, 
                      height: 120, 
                      mx: 'auto', 
                      mb: 2,
                      border: '4px solid white',
                      boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
                    }}
                  />
                  <Typography variant="h6" component="h3" gutterBottom fontWeight="bold">
                    {member.name}
                  </Typography>
                  <Typography 
                    variant="subtitle1" 
                    color="primary" 
                    gutterBottom
                    sx={{ fontWeight: 500 }}
                  >
                    {member.position}
                  </Typography>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    {member.bio}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>
      
      {/* Founder Spotlight Section */}
      <Box sx={{ bgcolor: 'grey.50', py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Grid container spacing={6} alignItems="center">
            <Grid item xs={12} md={5}>
              <Box
                component="img"
                src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&auto=format&fit=crop&w=1200&q=80"
                alt="Devang Sharma - Founder"
                sx={{
                  width: '100%',
                  borderRadius: 4,
                  boxShadow: '0 20px 40px rgba(0,0,0,0.15)',
                }}
              />
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="overline" color="primary" fontWeight="bold">
                FOUNDER SPOTLIGHT
              </Typography>
              <Typography variant="h2" sx={{ mb: 3, fontWeight: 700 }}>
                Devang Sharma
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, fontWeight: 500, color: 'text.secondary' }}>
                Visionary Founder & CEO
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                At just 27, Devang Sharma has already made significant waves in the restaurant technology space with QResto. His unique combination of design thinking, marketing acumen, and technological vision has positioned him as an emerging innovator in the industry.
              </Typography>
              <Typography variant="body1" paragraph sx={{ fontSize: '1.1rem' }}>
                With a background in UX/UI design and digital marketing, Devang brings a holistic approach to solving restaurant challenges. His ability to identify pain points and craft elegant, user-friendly solutions sets QResto apart in a competitive market.
              </Typography>
              <Typography variant="body1" sx={{ fontSize: '1.1rem' }}>
                Devang's leadership philosophy centers on continuous innovation balanced with practical implementation. He leads by example, bringing boundless creativity, strategic thinking, and a hands-on approach to every aspect of QResto's development and growth.
              </Typography>
            </Grid>
          </Grid>
        </Container>
      </Box>
      
      {/* Our Mission Section */}
      <Box
        sx={{
          bgcolor: 'primary.main',
          color: 'white',
          py: { xs: 8, md: 10 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth="md">
          <Typography variant="h3" gutterBottom fontWeight="bold">
            Our Mission
          </Typography>
          <Typography variant="h5" sx={{ maxWidth: '800px', mx: 'auto', lineHeight: 1.6, opacity: 0.9 }}>
            To revolutionize the dining experience by bridging the gap between traditional menus and digital innovation, making AR technology accessible to restaurants of all sizes across India.
          </Typography>
        </Container>
      </Box>
      
      <Footer />
    </>
  );
};

export default AboutPage;